(() => {
  const formatMoney = (cents) => {
    if (window.theme && theme.Currency && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD'
    }).format((cents || 0) / 100);
  };

  const getSelectedOptions = (root) => {
    const inputs = Array.from(root.querySelectorAll('[data-fast-option-input]'));
    const indexes = [...new Set(inputs.map((input) => input.dataset.optionIndex))];

    return indexes
      .sort((a, b) => Number(a) - Number(b))
      .map((index) => {
        const groupInputs = inputs.filter((input) => input.dataset.optionIndex === index);
        const radioInput = groupInputs.find((input) => input.type === 'radio');

        if (radioInput) {
          const checked = groupInputs.find((input) => input.checked);
          return checked ? checked.value : '';
        }

        return groupInputs[0] ? groupInputs[0].value : '';
      });
  };

  const findVariant = (variants, selectedOptions) => {
    return variants.find((variant) => {
      return selectedOptions.every((value, index) => variant.options[index] === value);
    });
  };

  const setActiveMedia = (section, mediaId) => {
    const mediaItems = section.querySelectorAll('[data-fast-media-item]');
    const thumbs = section.querySelectorAll('[data-fast-thumb]');

    mediaItems.forEach((item) => {
      const isActive = String(item.dataset.mediaId) === String(mediaId);
      item.hidden = !isActive;
      item.classList.toggle('is-active', isActive);
    });

    thumbs.forEach((thumb) => {
      const isActive = String(thumb.dataset.mediaId) === String(mediaId);
      thumb.classList.toggle('is-active', isActive);

      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  };

  const parseMoneyText = (text) => {
    if (!text) {
      return null;
    }

    const cleaned = text.replace(/\s/g, '').replace(/[^0-9,.-]/g, '');
    if (!cleaned) {
      return null;
    }

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);

    if (decimalIndex === -1) {
      const integerOnly = cleaned.replace(/[^0-9-]/g, '');
      return integerOnly ? Number(integerOnly) * 100 : null;
    }

    const integerPart = cleaned.slice(0, decimalIndex).replace(/[^0-9-]/g, '');
    const decimalPart = cleaned.slice(decimalIndex + 1).replace(/[^0-9]/g, '').slice(0, 2).padEnd(2, '0');

    if (!integerPart && !decimalPart) {
      return null;
    }

    return Number(`${integerPart || '0'}${decimalPart}`);
  };

  const updateDisplayedTotal = (root, fallbackPriceCents) => {
    const form = root.closest('form');
    const quantityInput = form.querySelector('[data-fast-quantity]');
    const priceNode = root.querySelector('[data-fast-price]');
    const totalNode = root.querySelector('[data-fast-total]');

    if (!priceNode || !totalNode) {
      return;
    }

    const quantity = quantityInput ? Math.max(1, Number(quantityInput.value) || 1) : 1;
    const displayedPriceCents = parseMoneyText(priceNode.textContent);
    const unitPriceCents = displayedPriceCents ?? fallbackPriceCents;

    if (typeof unitPriceCents === 'number') {
      totalNode.textContent = formatMoney(unitPriceCents * quantity);
    }
  };

  const syncStandardProductHooks = (root, variant) => {
    const form = root.closest('form');
    const originalSelector = form ? form.querySelector('[data-product-select]') : null;
    const currentVariantNode = root.querySelector('[data-current-variant-json]');
    const priceA11yNode = root.querySelector('[data-a11y-price]');
    const comparePriceA11yNode = root.querySelector('[data-compare-price-a11y]');
    const comparePriceNode = root.querySelector('[data-compare-price]');
    const hasComparePrice = Boolean(variant && variant.compare_at_price > variant.price);

    if (originalSelector && variant) {
      originalSelector.value = String(variant.id);
    }

    if (currentVariantNode && variant) {
      currentVariantNode.textContent = JSON.stringify(variant);
    }

    if (priceA11yNode) {
      priceA11yNode.setAttribute(
        'aria-hidden',
        hasComparePrice ? 'false' : 'true'
      );
    }

    if (comparePriceA11yNode) {
      comparePriceA11yNode.setAttribute(
        'aria-hidden',
        hasComparePrice ? 'false' : 'true'
      );
    }

    if (comparePriceNode) {
      comparePriceNode.classList.toggle('hide', !hasComparePrice);
    }
  };

  const dispatchStandardVariantEvents = (root, variant) => {
    const form = root.closest('form');
    const originalSelector = form ? form.querySelector('[data-product-select]') : null;
    const detail = { variant };

    root.dispatchEvent(new CustomEvent('variantChange', { detail }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail }));

    if (variant) {
      root.dispatchEvent(new CustomEvent('variantPriceChange', { detail }));
      root.dispatchEvent(new CustomEvent('variantSKUChange', { detail }));
    }

    if (originalSelector) {
      originalSelector.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const initAccordions = (container) => {
    container.querySelectorAll('.product-fast__details').forEach((details) => {
      if (details.dataset.fastAccordionReady === 'true') {
        return;
      }

      details.dataset.fastAccordionReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) {
          return;
        }

        const accordionScope = details.closest('.product-fast__blocks, .product-fast__left-stack');
        if (!accordionScope) {
          return;
        }

        accordionScope.querySelectorAll('.product-fast__details[open]').forEach((item) => {
          if (item !== details) {
            item.open = false;
          }
        });
      });
    });
  };

  const updateState = (root) => {
    const section = root.closest('.product-fast');
    const variantsNode = root.querySelector('[data-fast-variants]');
    const form = root.closest('form');
    const masterInput = form.querySelector('[data-fast-master-id]');
    const quantityInput = form.querySelector('[data-fast-quantity]');
    const priceNode = root.querySelector('[data-fast-price]');
    const comparePriceNode = root.querySelector('[data-fast-compare-price]');
    const skuNode = root.querySelector('[data-fast-sku]');
    const totalNode = root.querySelector('[data-fast-total]');
    const addToCartButton = root.querySelector('[data-fast-add-to-cart]');
    const addToCartText = root.querySelector('[data-fast-add-to-cart-text]');

    if (!variantsNode || !masterInput || !priceNode || !comparePriceNode || !addToCartButton || !addToCartText) {
      return;
    }

    const variants = JSON.parse(variantsNode.textContent);
    const selectedOptions = getSelectedOptions(root);
    let variant = null;

    if (selectedOptions.length) {
      variant = findVariant(variants, selectedOptions);
    } else if (root.dataset.currentVariantId) {
      variant = variants.find((item) => String(item.id) === root.dataset.currentVariantId);
    }

    if (!variant) {
      masterInput.value = '';
      addToCartButton.disabled = true;
      addToCartText.textContent = root.dataset.unavailableLabel;
      return;
    }

    masterInput.value = variant.id;
    root.dataset.currentVariantId = variant.id;
    priceNode.textContent = formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      comparePriceNode.textContent = formatMoney(variant.compare_at_price);
      comparePriceNode.classList.remove('is-hidden');
      comparePriceNode.classList.remove('hide');
      priceNode.classList.add('on-sale');
    } else {
      comparePriceNode.textContent = '';
      comparePriceNode.classList.add('is-hidden');
      comparePriceNode.classList.add('hide');
      priceNode.classList.remove('on-sale');
    }

    if (skuNode) {
      skuNode.textContent = variant.sku || '';
    }

    if (variant.available) {
      addToCartButton.disabled = false;
      addToCartText.textContent = root.dataset.addToCartLabel;
    } else {
      addToCartButton.disabled = true;
      addToCartText.textContent = root.dataset.soldOutLabel;
    }

    syncStandardProductHooks(root, variant);
    dispatchStandardVariantEvents(root, variant);

    updateDisplayedTotal(root, variant.price);

    if (variant.featured_media_id && section) {
      setActiveMedia(section, variant.featured_media_id);
    }

    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const initProduct = (root) => {
    if (root.dataset.fastReady === 'true') {
      return;
    }

    root.dataset.fastReady = 'true';

    root.querySelectorAll('[data-fast-option-input]').forEach((input) => {
      input.addEventListener('change', () => updateState(root));
    });

    const form = root.closest('form');
    const quantityInput = form.querySelector('[data-fast-quantity]');
    const priceNode = root.querySelector('[data-fast-price]');
    syncStandardProductHooks(root, null);
    if (quantityInput) {
      quantityInput.addEventListener('input', () => updateDisplayedTotal(root));
      quantityInput.addEventListener('change', () => {
        quantityInput.value = Math.max(1, Number(quantityInput.value) || 1);
        updateDisplayedTotal(root);
      });
    }

    if (priceNode) {
      const priceObserver = new MutationObserver(() => updateDisplayedTotal(root));
      priceObserver.observe(priceNode, { childList: true, characterData: true, subtree: true });
    }

    const section = root.closest('.product-fast');
    if (section) {
      section.querySelectorAll('[data-fast-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => setActiveMedia(section, thumb.dataset.mediaId));
      });
    }

    updateState(root);
  };

  const initProducts = (container) => {
    container.querySelectorAll('[data-fast-product-root]').forEach(initProduct);
    initAccordions(container);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProducts(document));
  } else {
    initProducts(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProducts(event.target);
  });
})();
